import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { BasilArrowRight } from "@/components/icons/basil";
import { cn } from "@shared/lib/utils";
import { useCollectionData } from "@/components/collection/useCollectionData";
import { Countdown } from "@/components/renewal/Countdown";
import { SpotlightCard } from "@/components/renewal/fx/SpotlightCard";
import { StatPair } from "@/components/renewal/system";
import { useMissions } from "@/lib/renewal/missions";
import { isOnboarded } from "@/lib/renewal/onboarding";
import { MOCK_INVENTORY_PACKS } from "@/lib/renewal/shop";

const CELL_FOCUS =
  "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70";

/** S2 행동 셀 — SpotlightCard hover + 44px 타깃 + 셀 전체 링크. gold = 액션 준비 강조. */
function Cell({
  to,
  ariaLabel,
  gold,
  children,
}: {
  to: string;
  ariaLabel: string;
  gold?: boolean;
  children: ReactNode;
}) {
  return (
    <SpotlightCard className="h-full rounded-2xl">
      <Link
        to={to}
        aria-label={ariaLabel}
        className={cn(
          "flex h-full min-h-11 items-center justify-between gap-4 rounded-2xl border px-4 py-3 transition-colors duration-200",
          gold
            ? "border-accent/40 bg-accent/10 hover:border-accent/60"
            : "border-line bg-(--ty-bg-2) hover:border-line-hi",
          CELL_FOCUS,
        )}
      >
        {children}
      </Link>
    </SpotlightCard>
  );
}

/**
 * 섹션 아키텍처 v2 — S2 TODAY: 히어로 직후 오늘의 행동 3셀(무료 팩 / 미션 /
 * 미개봉 팩). 구 MissionSignal·PersonalStrip 흡수. 미로그인 = 사인인 셀 1개
 * (모바일 전용 — 데스크톱은 top-bar ProfileChip이 사인인 담당, 구 PersonalStrip
 * 로직 그대로). 신규 미온보딩 유저는 ① 자리가 온보딩 시작 셀로 교체.
 */
export function TodayStrip({ onSignIn }: { onSignIn: () => void }) {
  const { authenticated, sessionPending, state: cardsState } = useCollectionData();
  const { missions } = useMissions();

  if (!authenticated) {
    if (sessionPending) return null;
    return (
      <div className="lg:hidden">
        <SpotlightCard className="rounded-2xl">
          <button
            type="button"
            onClick={onSignIn}
            className={cn(
              "flex min-h-11 w-full items-center justify-between gap-4 rounded-2xl border border-line bg-(--ty-bg-2) px-4 py-3 text-left transition-colors duration-200 hover:border-line-hi",
              CELL_FOCUS,
            )}
          >
            {/* Courtyard 버튼 위계 — Inter 14/600 (mono uppercase 폐지) */}
            <span className="text-sm font-semibold leading-snug text-ink-mute">
              <span className="text-accent">Sign in with Telegram</span>{" "}
              <span aria-hidden>→</span> track your IP, RP &amp; collection
            </span>
            <BasilArrowRight className="size-3.5 shrink-0 text-ink-mute" aria-hidden />
          </button>
        </SpotlightCard>
      </div>
    );
  }

  // 온보딩 신호 — 플래그 없음 + 소유 카드 0장 확인 시에만 (구 HomePage 조건 이관).
  const showOnboarding = !isOnboarded() && cardsState !== null && cardsState.ownedCount === 0;

  const inProgress = missions.filter((m) => m.status === "todo").length;
  const claimable = missions.filter((m) => m.status === "claimable").length;
  const unopened = MOCK_INVENTORY_PACKS.reduce((sum, pack) => sum + pack.count, 0);

  const dailyReady = cardsState?.dailyFreeAvailable === true;
  const nextDailyFreeAt = cardsState?.nextDailyFreeAt ?? null;

  return (
    <section aria-label="Today">
      {/* 홈 섹션 넘버링 눈썹 (scroll-asset-prd §2 A3) — UX 루프 순번 = 정보 */}
      <p className="ty-eyebrow mb-3">#1 Today</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {showOnboarding ? (
          <Cell to="/onboarding" ariaLabel="Start — pick your assistant" gold>
            <span className="flex items-center gap-2.5 text-sm font-semibold text-ink">
              <Sparkles className="size-4 shrink-0 text-accent" aria-hidden />
              Start — pick your assistant
            </span>
            <BasilArrowRight className="size-3.5 shrink-0 text-accent" aria-hidden />
          </Cell>
        ) : (
          <Cell
            to="/profile/packs"
            ariaLabel={
              dailyReady ? "Daily free pack — open now" : "Daily free pack — next timer"
            }
            gold={dailyReady}
          >
            <span className="ty-eyebrow text-[10px]">Daily free pack</span>
            <span className="flex items-center gap-3">
              {dailyReady ? (
                <span className="text-sm font-semibold text-accent">Open now</span>
              ) : (
                <span className="flex min-w-0 flex-col items-end gap-0.5 text-right">
                  <span className="text-xs text-ink-mute">Next in</span>
                  {nextDailyFreeAt ? (
                    <Countdown
                      to={nextDailyFreeAt}
                      className="font-mono text-sm font-medium tabular-nums text-ink"
                    />
                  ) : (
                    <span className="font-mono text-sm font-medium tabular-nums text-ink">—</span>
                  )}
                </span>
              )}
              <BasilArrowRight
                className={cn("size-3.5 shrink-0", dailyReady ? "text-accent" : "text-ink-mute")}
                aria-hidden
              />
            </span>
          </Cell>
        )}

        <Cell
          to="/profile/missions"
          ariaLabel={`Missions — ${inProgress} in progress, ${claimable} to claim`}
          gold={claimable > 0}
        >
          <span className="flex items-center gap-2">
            <span className="ty-eyebrow text-[10px]">Missions</span>
            {claimable > 0 && (
              <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-danger" />
            )}
          </span>
          <span className="flex items-center gap-5 sm:gap-6">
            <StatPair label="In progress" value={inProgress} align="right" />
            <StatPair label="Claimable" value={claimable} align="right" />
            <BasilArrowRight className="size-3.5 shrink-0 text-ink-mute" aria-hidden />
          </span>
        </Cell>

        {unopened > 0 ? (
          <Cell to="/profile/packs" ariaLabel={`Unopened packs — ${unopened} packs`}>
            <span className="ty-eyebrow text-[10px]">Unopened packs</span>
            <span className="flex items-center gap-3">
              <StatPair label="Ready to open" value={unopened} unit="packs" align="right" />
              <BasilArrowRight className="size-3.5 shrink-0 text-ink-mute" aria-hidden />
            </span>
          </Cell>
        ) : (
          <Cell to="/profile/missions" ariaLabel="No unopened packs — earn packs from missions">
            <span className="ty-eyebrow text-[10px]">Unopened packs</span>
            <span className="flex items-center gap-3">
              <span className="text-sm font-semibold text-ink">Earn packs</span>
              <BasilArrowRight className="size-3.5 shrink-0 text-ink-mute" aria-hidden />
            </span>
          </Cell>
        )}
      </div>
    </section>
  );
}
